/**
 * Echo Hunt -- the Characters tab (P8, rewritten for §8.7 in P10.5).
 *
 * A character is a PERSISTENT entry in `echoProfiles.characters` (§8.7), not something a story invents.
 * This tab is therefore a manager: the user's cast, authored by hand and/or drafted field-by-field by
 * the `loreGenerator` role, and picked from when a story starts.
 *
 * ## What the panel has to make visible
 *
 * 1. **The disposition axis**. "Characters with strong values (cautious vs. opportunistic), since acting
 *    true to personality is what carries the reading interest." (§8.2) The number is shown with the same
 *    prose the WEAVER is given (`DescribeCautionVsOpportunity`), so what the panel says and what the
 *    prompt says cannot drift apart.
 * 2. **Who this edit affects**. A story stores only `characterId` (§8.7), so editing an entry really does
 *    change the running story. The row shows the story count, and deleting a used character asks first
 *    (naming the stories) rather than silently breaking one.
 * 3. **The autogen boundary**. The draft buttons fill ONE field at a time, plus a whole-draft button for
 *    an empty entry. Every draft is steerable with a free-text hint. With no usable model the buttons
 *    still work -- they fall back to the rng pools in `Worlds.ts` -- and the form says so instead of
 *    pretending a model answered.
 *
 * ## Why notes are not validated against the disposition
 *
 * `CreateCharacter` composes notes FROM the disposition, so a generated entry cannot contradict itself
 * (§16.2 P8). A HAND-WRITTEN character may, and that is the user's business: the weaver is simply told
 * what the entry says. The invariant is a generator property, not a runtime constraint (§8.7).
 */
import React, {useState} from "react";
import {observer_mgl} from "mobx-graphlink";
import {Button, CheckBox, Column, Row, Text, TextArea, TextInput} from "react-vcomponents";
import {ES, InfoButton} from "web-vcore";
import {ScrollView} from "react-vscrollview";
import {ShowMessageBox} from "react-vmessagebox";
import {GetMyEchoProfile} from "../../../EchoHunt/Profiles_Live.js";
import {
	CountStoriesUsingCharacter_Live,
	DraftCharacter_Live,
	GetCharacterPool_Live,
	GetStoriesUsingCharacter_Live,
	RemoveCharacter_Live,
	UpsertCharacter_Live,
} from "../../../EchoHunt/Characters_Live.js";
import {ApplyCharacterDraft, ClampDisposition, NewCharacter} from "../../../EchoHunt/Lore.js";
import {LoreCharacterField} from "../../../EchoHunt/Prompts/Lore.js";
import type {CharacterDraftKey} from "../../../EchoHunt/Prompts/Lore.js";
import {EchoCharacter} from "../../../EchoHunt/Types.js";
import {DescribeCautionVsOpportunity} from "../../../EchoHunt/Prompts/Weave.js";

export const CharactersPanel = observer_mgl(()=>{
	const profile = GetMyEchoProfile();

	return (
		<ScrollView style={ES({flex: 1, padding: 10})} contentStyle={{display: "flex", flexDirection: "column"}}>
			<Row>
				<Text>Echo Hunt -- Characters</Text>
				<InfoButton ml={5} text={[
					"§8.7: your cast. A story PICKS one of these and keeps only its id, so editing a character changes",
					"the story that is running -- the count beside each entry is there to make that obvious.",
					"",
					"§8.2: characters with STRONG values, because acting true to personality is what carries the reading",
					"interest -- and because the character's ordinary reasoning applied to impossible events is the model",
					"of the failure the game trains against.",
					"",
					"The disposition axis runs -1 (cautious) to +1 (opportunistic). It is the one number the weaver is",
					"told about, so it is shown with the same wording the prompt uses.",
					"",
					"Drafting uses the lore generator role: it can fill one field or the whole entry, steered by the hint",
					"box. Generated notes follow from the disposition; if you write your own, contradict it freely -- that",
					"is your call, and the weaver is simply told what the entry says.",
					"",
					"The pool starts empty on purpose and is not auto-seeded. A story cannot start without a character.",
				].join("\n")}/>
			</Row>

			{profile == null
				? <Text mt={10} style={{opacity: .7}}>Create an Echo Hunt profile (Settings tab) to edit your cast.</Text>
				: <CharacterPoolEditor/>}
		</ScrollView>
	);
});

const CharacterPoolEditor = observer_mgl(()=>{
	const pool = GetCharacterPool_Live();
	// the working draft for a NEW character; null = the add form is closed
	const [draft, setDraft] = useState<EchoCharacter|null>(null);

	return (
		<Column mt={10} style={{gap: 8}}>
			<Row center style={{gap: 8}}>
				<Text style={{fontWeight: "bold"}}>Your cast</Text>
				<Text style={{opacity: .8}}>{`${pool.length} character(s)`}</Text>
				<Button text="Add character" onClick={()=>setDraft(NewCharacter())}/>
			</Row>

			{pool.length == 0 && draft == null &&
				<Text style={{color: "orange"}}>
					No characters yet. A round needs one to follow -- add one here, and the draft buttons can fill it in.
				</Text>}

			{draft != null &&
				<CharacterEditor
					key={draft.id}
					character={draft}
					isNew={true}
					onSave={async character=>{
						await UpsertCharacter_Live(character);
						setDraft(null);
					}}
					onCancel={()=>setDraft(null)}/>}

			{pool.map(entry=>
				<CharacterCard key={entry.id} character={entry}/>)}
		</Column>
	);
});

const CharacterCard = observer_mgl((props: {character: EchoCharacter})=>{
	const {character} = props;
	const [editing, setEditing] = useState(false);
	const storyCount = CountStoriesUsingCharacter_Live(character.id);

	if (editing) {
		return (
			<CharacterEditor
				character={character}
				isNew={false}
				onSave={async updated=>{
					await UpsertCharacter_Live(updated);
					setEditing(false);
				}}
				onCancel={()=>setEditing(false)}/>
		);
	}

	return (
		<Column style={{padding: 8, borderRadius: 4, background: "rgba(255,255,255,.04)"}}>
			<Row center style={{gap: 8, flexWrap: "wrap"}}>
				<Text style={{fontWeight: "bold"}}>{character.name.length > 0 ? character.name : "(no name yet)"}</Text>
				<Text style={{fontSize: 11, opacity: .6}}>{`disposition: ${character.values_cautionVsOpportunity}`}</Text>
				<Text style={{fontSize: 11, opacity: .6}}>{DescribeCautionVsOpportunity(character.values_cautionVsOpportunity)}</Text>
				{character.generated && <Text style={{fontSize: 11, opacity: .45}}>drafted</Text>}
				{storyCount > 0 &&
					<Text style={{fontSize: 11, color: "orange"}}>
						{`used by ${storyCount} stor${storyCount == 1 ? "y" : "ies"}`}
					</Text>}
				<Button text="Edit" onClick={()=>setEditing(true)}/>
				<Button text="Remove" onClick={async()=>{
					await ConfirmRemoveCharacter(character);
				}}/>
			</Row>
			{character.tagline.length > 0 &&
				<Text style={{fontSize: 12, opacity: .85, marginTop: 2}}>{character.tagline}</Text>}
			<Text style={{fontSize: 12, opacity: .75, marginTop: 2}}>{character.personalityNotes}</Text>
		</Column>
	);
});

/** A dialog because deleting a character that a story is using breaks that story (§8.7): the story keeps
 * only the id, so once the entry is gone the story has no character and cannot be played until one is
 * picked. The message NAMES the affected stories rather than saying "1 story", so the warning is
 * actionable, and it is skipped entirely when nothing uses the entry. */
async function ConfirmRemoveCharacter(character: EchoCharacter): Promise<void> {
	const stories = GetStoriesUsingCharacter_Live(character.id);

	if (stories.length == 0) {
		await RemoveCharacter_Live(character.id);
		return;
	}

	ShowMessageBox({
		title: "Remove character", cancelButton: true,
		message: [
			`"${character.name || character.id}" is used by ${stories.length} stor${stories.length == 1 ? "y" : "ies"}:`,
			...stories.map(story=>`- ${new Date(story.createdAt).toLocaleString()}${story.ended == true ? " (ended)" : " (running)"}`),
			"",
			"A story cannot continue without a character: it will need a new one before another round can be built.",
			"",
			"Remove it anyway?",
		].join("\n"),
		onOK: async()=>{
			await RemoveCharacter_Live(character.id);
		},
	});
}

/** The add/edit form. Local state, committed by Save -- so a half-typed character never reaches the DB,
 * which matters because a story may already be following the entry being edited. */
const CharacterEditor = observer_mgl((props: {
	character: EchoCharacter,
	isNew: boolean,
	onSave: (character: EchoCharacter)=>Promise<void>,
	onCancel: ()=>void,
})=>{
	const {isNew, onSave, onCancel} = props;

	const [draft, setDraft] = useState<EchoCharacter>(props.character);
	const [hint, setHint] = useState("");
	const [override, setOverride] = useState(false);
	const [busyField, setBusyField] = useState<LoreCharacterField|null>(null);
	const [message, setMessage] = useState<string|null>(null);

	const change = (updates: Partial<EchoCharacter>)=>{
		setDraft({...draft, ...updates});
	};

	/** Drafts one field (or the whole entry) and merges ONLY that field into the local draft.
	 *
	 * `ApplyCharacterDraft` is the pure helper that enforces "only the requested field changes"; merging
	 * by spreading the model's reply here would let a stray rewrite of the disposition land in the editor
	 * while the user believes they only asked for a name. Nothing is persisted until Save. */
	const draftField = async(field: LoreCharacterField)=>{
		setBusyField(field);
		setMessage(null);
		try {
			const result = await DraftCharacter_Live({base: draft, field, override, hint});
			const merged = ApplyCharacterDraft(draft, result.draft, result.keys as CharacterDraftKey[]);
			// keep the local id -- the pool entry's identity is the editor's, not the model's
			setDraft({...merged, id: draft.id, generated: merged.generated});
			if (result.keys.length == 0) {
				// Override is off and every field is already filled. This is the case that used to look like a
				// broken button: the draft changed nothing because there was nothing missing to fill.
				setMessage("Every field is already filled in. Tick Override to redraft them anyway.");
			} else if (!result.generatedByModel) {
				setMessage(result.error != null
					? `Used the offline generator (${result.error})`
					: "Used the offline generator -- no model returned a draft.");
			}
		} catch (ex) {
			setMessage(`Drafting failed: ${ex}`);
		}
		setBusyField(null);
	};

	const busy = busyField != null;
	const canSave = draft.name.trim().length > 0 && draft.personalityNotes.trim().length > 0;

	return (
		<Column style={{gap: 5, padding: 8, borderRadius: 4, background: "rgba(255,255,255,.07)"}}>
			<Row center style={{gap: 6, flexWrap: "wrap"}}>
				<Text style={{fontWeight: "bold"}}>{isNew ? "New character" : "Edit character"}</Text>
				<Button text={busyField == "all" ? "Drafting…" : "Draft all fields"} enabled={!busy}
					onClick={()=>draftField("all")}/>
				<CheckBox text="Override all" value={override} onChange={setOverride}
					title="Redraft EVERY field, replacing what is already there. Without this, a draft only fills empty fields."/>
				<TextInput style={{width: 320}} value={hint} onChange={setHint}
					placeholder="optional steer, e.g. retired lighthouse keeper, quietly stubborn"/>
			</Row>

			<Row center style={{gap: 6}}>
				<Text style={{width: 70}}>Name</Text>
				<TextInput width={240} value={draft.name} onChange={value=>change({name: value})}/>
				<Button text="Draft" enabled={!busy} onClick={()=>draftField("name")}/>
			</Row>

			<Row center style={{gap: 6}}>
				<Text style={{width: 70}}>Tagline</Text>
				<TextInput width={380} value={draft.tagline} onChange={value=>change({tagline: value})}
					placeholder="one line for the list, e.g. 'Night porter, keeps a map of every corridor.'"/>
				<Button text="Draft" enabled={!busy} onClick={()=>draftField("tagline")}/>
			</Row>

			<Row center style={{gap: 6, flexWrap: "wrap"}}>
				<Text style={{width: 70}}>Disposition</Text>
				<TextInput width={80} value={`${draft.values_cautionVsOpportunity}`} onChange={value=>{
					const parsed = Number(value);
					change({values_cautionVsOpportunity: Number.isFinite(parsed) ? parsed : 0});
				}} placeholder="-1..1"/>
				<Button text="-1" title="Very cautious" onClick={()=>change({values_cautionVsOpportunity: -1})}/>
				<Button text="0" onClick={()=>change({values_cautionVsOpportunity: 0})}/>
				<Button text="+1" title="Very opportunistic" onClick={()=>change({values_cautionVsOpportunity: 1})}/>
				<Button text="Draft" enabled={!busy} onClick={()=>draftField("disposition")}/>
				<Text style={{fontSize: 11, opacity: .6}}>{DescribeCautionVsOpportunity(ClampDisposition(draft.values_cautionVsOpportunity))}</Text>
			</Row>

			<Row center style={{gap: 6, alignItems: "flex-start"}}>
				<Text style={{width: 70}}>Notes</Text>
				<TextArea value={draft.personalityNotes} onChange={value=>change({personalityNotes: value})}
					style={{width: 520, height: 90, resize: "vertical"}}/>
				<Button text="Draft" enabled={!busy} onClick={()=>draftField("notes")}/>
			</Row>

			<Text style={{fontSize: 11, opacity: .55}}>
				Notes are what the weaver obeys: concrete HABITS ("reads a route before entering a place"), not
				adjectives. Drafted notes follow from the disposition; hand-written ones may contradict it if you want.
			</Text>

			{message != null && <Text style={{fontSize: 11, color: "orange"}}>{message}</Text>}

			<Row style={{gap: 6}}>
				<Button text="Save" enabled={!busy && canSave}
					title={canSave ? undefined : "A name and at least one personality note are required."}
					onClick={async()=>{
						await onSave({
							...draft,
							name: draft.name.trim(),
							tagline: draft.tagline.trim(),
							values_cautionVsOpportunity: ClampDisposition(draft.values_cautionVsOpportunity),
							personalityNotes: draft.personalityNotes.trim(),
						});
					}}/>
				<Button text="Cancel" onClick={onCancel}/>
			</Row>
		</Column>
	);
});
